import { v4 as uuidv4 } from 'uuid';

import {copyObject, findConnection, isValueEmpty} from '../../libs';
import Noun from '../../core/Noun';
import LinkList from '../LinkList';
import RelationshipList from '../RelationshipList';
import Meta from '../Meta';
import Collection from '../Collection';
import ResourceList from '../ResourceList';
import Connection from '../Connection';
import Link from '../Link';
import GenericError from "../../errors/GenericError";
import Configuration from "../Configuration";
import MetaField from "../MetaField";
import {
  initExclusionList,
  updateExclusionList,
} from "./excludedUnless"


class ValidationError extends Error {
  constructor(errorCode, reason, details) {
    super(reason);

    this.errorCode = errorCode;
    this.reason = reason;
    this.details = details;
  }
}

class RequiredValueError extends ValidationError {
  constructor(attributeName) {
    super('required-attribute', `${attributeName} is required.`, {
      attribute: attributeName,
    });
  }
}

const compareAttribute = (field, alpha, beta) => {
  const fieldType = field && field.attr("type");
  switch (fieldType) {
    case 'date':
    case 'datetime': {
      return Object.is(new Date(alpha).toJSON(), new Date(beta).toJSON());
    }
    case 'array':
    case 'jsArray': {
      return Array.isArray(alpha) && Array.isArray(beta) && alpha.length === beta.length && alpha.every((val, index) => Object.is(val, beta[index]));
    }
    default: {
      return Object.is(alpha, beta);
    }
  }
};

// Look for all attributes that you are dependent on
function updateAncestorChain(attributeUpdates, key) {
  // Get the field for the current attribute
  const field = this.meta().field(key);

  // Don't try to set any ancestor fields for multipicklists.
  if (field.attr('type') === 'multipicklist') {
    return attributeUpdates;
  }

  // check if it is dependent on some other attribute (IE. Source Type from Source)
  const depends = field.attr('depends');
  if (!depends) return attributeUpdates;

  // Get the newly selected option for an attribute and check to see which ancestors it is allowed to have.
  const updatedOption = field.options().item(attributeUpdates[key]);

  if (!updatedOption) {
    throw new GenericError('invalid-picklist-option', 'Option does not exist in picklist meta options', {
      attrName: key,
      optionId: attributeUpdates[key],
    });
  }

  const possibleOptions = updatedOption.attr('depends');

  // If the currently selected value is an option then just leave everything above alone and pull out.
  // In this case there is no need to continue traversing upwards.
  const selectedValueForAncestor = this.attr(depends);
  if (!possibleOptions || possibleOptions.includes(selectedValueForAncestor)) {
    return attributeUpdates;
  }

  // if it is only allowed to have 1 ancestor, then choose it.
  if (possibleOptions.length === 1) {
      attributeUpdates[depends] = possibleOptions[0];

      // Remove all values down the tree for this ancestor that have not been explicitly set.
      attributeUpdates = updateDescendantChain.call(this, attributeUpdates, depends);
      return updateAncestorChain.call(this, {
        ...attributeUpdates,
        [depends]: possibleOptions[0],
      }, depends);
  }

  // Otherwise you are trying to update a child to be something that its parent does not allow.
  // This means you are doing something silly and may need to be told that you are doing something
  // that may not be better for the realm.
  throw new ValidationError(
    'ambiguous-ancestor',
    'Unable to select this dependent value that has an ambiguous ancestor',
    {
    childAttributeName: key,
    ancestorAttributeName: depends,
    ancestorValue: this.attr(depends),
    acceptableOptionsForChild: possibleOptions,
  });
}

function updateDescendantChain(attributeUpdates, key) {
  /**
   * clearInvalidValue
   * @param attrName Required. The attribute name
   * @param attributeUpdates Required. The pending updates attributes.
   * @returns bool. This will return true if a value was cleared.
   */
  function clearInvalidValue(attrName, attributeUpdates) {
    const currValue = this.attr(attrName);
    const dependsKey = this.meta().field(attrName).attr('depends');

    if (!dependsKey) {
      throw new GenericError('expected-depends-key', 'Expected a depends key in clearInvalidValue. Should not come in here if not. This indicates that someone has changed how updateDescendantChain works.', {
        resourceType: this.type(),
        attrName: attrName,
      });
    }

    // If a value is already
    if (currValue === undefined || currValue === '') {
      return;
    }

    // Check to see if a value is still valid based on what its dependent value has been set to. If it is no longer valid then clear it.
    switch(this.meta().field(attrName).attr('type')) {
      case 'picklist':
        // This will return the valid ancestors for your current key.
        // For instance, if this resource had city and state as attributes and we are
        // currently looking at "city", selectedValidAncestors would be set to ["maryland"].
        // However, if you had selected "bristol" as a city then you could have either Virginia or Tennessee as valid states
        // so selectedValidAncestors would be set to ["virginia", "tennessee"].
        const selectedValidAncestors = this.selectedOptions(attrName, 'depends')[0];

        // If this dependent value is still valid based what its parent has been set to then just leave it alone.
        const selectedAncestor = attributeUpdates[dependsKey];
        if (selectedValidAncestors && selectedValidAncestors.includes(selectedAncestor)) {
          return;
        }

        attributeUpdates[attrName] = "";
        break;
      case 'multipicklist':
        // We do not try to intelligently handle multipicklists. Just clear them if they are a descendant.
        attributeUpdates[attrName] = [];
        break;
      default:
        throw new Error(`Unhandled type ${this.meta().field(attrName).attr('type')}`);
    }
  }

  /**
   * queueDescendants
   * @param queue Required. The queue of nodes to visit
   * @param visited Required. A mapping of nodes that have already been visited.
   * @param attrName Required. The attribute that descendants should be queued for.
   * @returns void. Nothing is returned. This mutates queue and visited which are passed by reference.
   */
  function queueDescendants(queue, visited, attrName) {
    let template = this.meta().template().list({ ignoreFilters: true });

    for (let i = 0; i < template.length; i++) {
      const field = template[i];
      const name = field.attr('name');

      if (!visited[name] && field.attr('depends') === attrName) {
        queue.push(name);
      }
    }

    return queue;
  }

  let queue = [];
  let visited = {
    [key]: 1,
  };

  // Consider any attributes already set as visited. We don't want to unset those values.
  Object.keys(attributeUpdates).forEach(k => visited[k] = 1);

  // For the current value we just need to queue up any of its descendants
  queueDescendants.call(this, queue, visited, key);

  // BFS over dependency graph to find all children and unset children
  while (queue.length) {
    const attrName = queue.shift();

    clearInvalidValue.call(this, attrName, attributeUpdates);

    queueDescendants.call(this, queue, visited, attrName);
  }

  return attributeUpdates;
}

class Resource extends Noun {
  constructor(args = {}) {
    super(args);

    const {
      data = {},
      metaOverrideSet,
    } = args;

    if (typeof data !== 'object') {
      throw new Error('Invalid resource transfer object: must be an object');
    }

    const {
      id,
      type,
      attributes = {},
      meta,
      relationships = {},
      links = {},
    } = data;

    this.resourceInstanceId = uuidv4();
    this.metaOverrideSet = metaOverrideSet;
    this._changedValues = new Map();
    this.resourceId = id;
    this.resourceType = type;
    this._exclusionList = {};

    this.hasOwnMeta = false;
    const parentCollection = this.getParentCollection();
    if (!meta && this.hasParentCollection()) {
      this.initialMeta = parentCollection.initialMeta;
      this.metaInstance = parentCollection.meta();
    } else if (false && meta && this.hasParentCollection()) { // Disable meta merging for now
      this.initialMeta = copyObject(meta, 'initial resource meta');
      const resourceMeta = new Meta({data: meta, parent: this, metaOverrideSet});

      const collectionMetaRaw = parentCollection.meta().export();

      const collectionMeta = new Meta({ data: collectionMetaRaw, parent: this, metaOverrideSet });

      // Attempt to merge meta. In the event of an issue revert to original behaviour
      try {
        this.metaInstance = collectionMeta.merge(resourceMeta);

      } catch (err) {
        const outerError = new Error('Error merging meta during resource construction');
        outerError.stack = `${outerError.stack}\n${err.stack}`;
        Configuration.onSilentError(outerError);
        this.metaInstance = new Meta({ data: meta, parent: this, metaOverrideSet });
      }

      this.hasOwnMeta = true;
    } else {
      this.initialMeta = copyObject(meta, 'initial resource meta');
      this.metaInstance = new Meta({data: meta, parent: this, metaOverrideSet});
      this.hasOwnMeta = true;
    }

    // Set attributes
    let _attributeMap = {};

    this.meta().template().list({ignoreFilters: true}).forEach((field) => {
      const fieldName = field.attr('name');

      if (field.attr('type') === 'array') {
        let fieldData = attributes[fieldName] || [];
        if (field.attr('hint') === 'jsArray') {
          // Nested jsArrays only export with the attributes and cannot be submitted directly
          // However, we need unique IDs to be able to add them to the map
          fieldData = attributes[fieldName] ? attributes[fieldName].map((v, i) => ({
            id: i,
            attributes: v,
          })) : [];
        } else if (field.attr('hint') === 'jsObject') {
          // We export jsObjects as objects instead of arrays. We need to put these back when reimporting
          fieldData = attributes[fieldName] ? [{
            attributes: attributes[fieldName],
          }] : [];
        }
        _attributeMap[fieldName] = new Collection({
            data: {
              data: fieldData,
              meta: field.attr('arrayDef'),
            },
            metaOverride: null,
            metaOverrideSet: field.attr('_override') || metaOverrideSet,
            parent: this,
          },
        );
        if (field.attr('min') && field.attr('min') > _attributeMap[fieldName].resources().length()) {
          while (_attributeMap[fieldName].resources().length() < field.attr('min')) {
            _attributeMap[fieldName].create({ addToCollection: true, useCollectionMetaOverrideSet: true });
          }
        }
      } else if (field.attr('type') === 'collection') {
        _attributeMap[fieldName] = new Collection({
            data: attributes[fieldName],
            metaOverride: null,
            metaOverrideSet: field.attr('_override') || metaOverrideSet,
            parent: this,
          },
        );
      } else {
        _attributeMap[fieldName] = attributes[fieldName] || field.defaultValue();
      }
    });

    // copy in attributes that haven't been added by meta yet
    Object.keys(attributes).forEach(k => {
      if (_attributeMap[k] === undefined) {
        _attributeMap[k] = attributes[k];
      }
    });

    // NOTE: Leaving this in even though it is unused to save future me (or someone else) some time debugging
    //       why this is slow.

    // Always remember the attributes at time of construction.
    // Copying the entire _attributeMap after building it is very slow because it will
    // build out sub-collections with meta.
    // this.initialAttributes = copyObject(attributes, 'resource initial attributes (as transfer object)');

    this._attributeMap = _attributeMap;

    this._exclusionList = initExclusionList.call(this);

    this.linkList = new LinkList({ data: links, parent: this, defaultExport: this });
    this._relationshipList = new RelationshipList({ data: relationships, parent: this, defaultExport: this });
  }

  static getParentSelfLink(resource) {
    let parent = resource.parent();

    // This will be true if the resource was added to a collection when it was created.
    // Go up one more level to get the Collection
    if (parent instanceof ResourceList) {
      parent = parent.parent();
    }

    if (!parent) {
      throw new Error('Parent must be set');
    }

    const parentSelfLink = parent.link('self');
    if (!parentSelfLink) {
      throw new Error('Parent must be have a self link');
    }

    return parentSelfLink;
  }

  instanceId() {
    return this.resourceInstanceId;
  }

  connection(connectionOrCookie) {
    if (!connectionOrCookie) return this.linkConnection ? this.linkConnection : findConnection(this.parent());

    if (connectionOrCookie instanceof Connection) {
      this.linkConnection = connectionOrCookie;
    } else {
      this.linkConnection = new Connection({ data: { authToken: connectionOrCookie }});
    }

    return this.linkConnection;
  }

  // Overrides Noun
  attr(key, value) {
    if (key && value && !this._changedValues.has(key)) {
      this._changedValues.set(key, this._attributeMap[key]);
    }

    if (key === undefined) {
      throw new Error('Usage: Key is required.');
    }

    if (value === undefined) {
      return this._attributeMap[key];
    }

    // If there is no update, then just short circuit.
    if (value === this._attributeMap[key]) {
      return this;
    }

    let attributeUpdates = {
      [key]: value,
    };

    if (this.meta()) {
      // Clear out all descendent values
      attributeUpdates = updateDescendantChain.call(this, attributeUpdates, key);

      // Attempt to update parents (and deal with affected descendents of ancestors)
      attributeUpdates = updateAncestorChain.call(this, attributeUpdates, key);
    }

    this._attributeMap = {
      ...this._attributeMap,
      ...attributeUpdates,
    };

    updateExclusionList.call(this, key)

    return this;
  }

  attributes(attrs) {
    // The attr method on resource is quite special because of dependent updates. It must be invoked in order to update attributes.
    if (attrs === undefined) {
      return super.attributes();
    }

    console.error(`Resource attributes should be updated via the 'attr' method. When you change an attribute, it could cause other attributes to change - for example, picklists that have depends, or anything with exclude unless meta. Using attributes lets you change 2+ things at a time, but really due to meta some of your changes could be ignored. For example, attributes({city: 'New York City', state: 'CA'}) looks like it sets both of those attributes, but if city depends on state, then city's value will be removed`);
    const attrNames = Object.keys(attrs);

    for (let i = 0; i < attrNames.length; i++) {
      const attrName = attrNames[i];
      this.attr(attrName, attrs[attrName]);
    }

    return this;
  }

  copy() {
    return copyObject(this, `resource (id: ${this.id()} type:${this.type()})`);
  }

  dirtyAttributes() {
    const dirtyAttributes = [];
    for (const [k,v] of this._changedValues.entries()) {
      if (!compareAttribute(this.meta().field(k), v, this.attr(k))) dirtyAttributes.push([k, v]);
    }
    return dirtyAttributes;
  }

  isDirty() {
    return this.dirtyAttributes().length !== 0;
  }

  // Overrides Noun
  export(args = {}) {
    const {
      // exportAttributesAsStrings is the default behavior. If this is true, all attributes will be exported as strings
      //  If it is false, attributes will be exported according to their meta type
      exportAttributesAsStrings = true,
      // exportAttributesAsIs will export attributes without any transformations
      exportAttributesAsIs = false,
      dangerouslyAllowObjectExport = false
    } = args;

    // exportAttributesAsIs trumps exportAttributesAsStrings
    const exportAsString = exportAttributesAsStrings && !exportAttributesAsIs;

    const resourceMeta = this.meta();

    function exportAttribute(attributeValue, attrKey) {
      if(resourceMeta.field(attrKey).attr('_exportFunction')){
        const func = resourceMeta.field(attrKey).attr('_exportFunction')
        if (typeof func !== 'function') {
          throw new Error('Export Function must be a function')
        }

        return func(attributeValue)
      }

      // If a primitive, or a collection then we got this and we can export.
      if (typeof attributeValue !== 'object') {
        if (exportAttributesAsIs) {
          return attributeValue;
        }

        // We'll export all attributes as strings by default
        // You can specify export a string on meta override and it'll be honored here.
        if (exportAsString) {
          return String(attributeValue);
        }

        // Otherwise we'll export them as the appropriate type given the meta
        if (resourceMeta.field(attrKey).attr('type') === 'number') {
          return parseFloat(attributeValue);
        }

        if (resourceMeta.field(attrKey).attr('type') === 'bool') {
          return Boolean(attributeValue);
        }

        return attributeValue;
      }

      // Collections know how to export themselves.
      if (attributeValue instanceof Collection) {
        if (resourceMeta.field(attrKey).attr('type') === 'collection') {
          return attributeValue.export(args); // Type collection supports the entire collection
        } else {
          // If the collection is a jsObject, and there aren't any resources, don't try to export it
          if (resourceMeta.field(attrKey).attr('hint') === 'jsObject') {
            if (attributeValue.resources().length() === 0) {
              return null;
            }
            // Otherwise, we export the attributes of the first and only resource
            return attributeValue.resources().head().export(args).attributes;
          } else if (resourceMeta.field(attrKey).attr('hint') === 'jsArray') {
            // If it's a js array, we'll export the attributes of each sub resource
            return attributeValue.resources().list().map((obj) => {
              if (typeof obj === 'object') {
                return obj.export(args).attributes;
              }
            });
          }
          return attributeValue.export(args).data; // Only return data when exporting a collection into a resource
        }
      }

      // If an array, then it best be an array of primitives or else it's error city.
      if (Array.isArray(attributeValue)) {
        return attributeValue.map((subValue) => {
          if (typeof subValue === 'object' && !dangerouslyAllowObjectExport) {
            throw new Error(`Unexpected object as attribute array value: ${JSON.stringify(subValue)}`);
          }
          return exportAsString ? String(subValue) : subValue;
        });
      }
      return attributeValue
    }

    const attributes = Object.keys(this._attributeMap)
      .filter(key => !this._exclusionList[key])
      .reduce((root, nextAttributeKey) => {
        const attributeValue = this._attributeMap[nextAttributeKey];
        if (attributeValue === null || attributeValue === undefined) {
          return root; // if the meta for the value doesn't exist, do not add the attribute to request
        }

        const exportedValue = exportAttribute(attributeValue, nextAttributeKey);
        // If there was no value for the jsObject, don't even include the key for it instead of setting the key value to
        // null
        if (resourceMeta.field(nextAttributeKey).attr('hint') === 'jsObject' && exportedValue == null) {
          return {
            ...root,
          }
        }
        return {
          ...root,
          [nextAttributeKey]: exportedValue,
        };
      }, {});

    const exportedResource = {
      id: this.resourceId,
      type: this.resourceType,
      links: this.linkList.export(),
      relationships: this._relationshipList.export(),
      attributes,
      meta: this.hasOwnMeta ? this.initialMeta : undefined, // Exported with meta used at time of construction
    };

    if (this._etag) {
      exportedResource.etag = this._etag;
    }

    return exportedResource;
  }

  id() {
    return this.resourceId;
  }

  links() {
    return this.linkList;
  }

  link(key) {
    if (key === undefined) {
      throw new Error('Usage: Key is required.');
    }

    const link = this.linkList.item(key);

    if (!link) return null;

    return link;
  }

  log(prefix, logger) {
    logger.log(`${prefix}.id: `, this.id());
    logger.log(`${prefix}.type: `, this.type());

    for (const link of this.links().list()) {
      logger.log(`${prefix}.link.${link.attr('name')}: `, link.attr('url'));
    }

    for (const relationship of this.relationships().list()) {
      for (const link of relationship.links().list()) {
        logger.log(`${prefix}.relationship.${relationship.attr('name')}.link.${link.attr('name')}: `, link.attr('url'));
      }
    }

    const attributeKeys = Object.keys(this.attributes());

    for (const attrName of attributeKeys) {
      const attrValue = this.attr(attrName);

      // Then we have a collection
      if (attrValue instanceof Collection) {
        attrValue.log(`${prefix}.attr.${attrName}`, logger);
      } else {
        const maxLength = 200;
        const strAttr = JSON.stringify(attrValue);
        if (strAttr && strAttr.length > maxLength) {
          logger.log(`${prefix}.attr.${attrName}: ${strAttr.slice(0, maxLength)}... + ${strAttr.length - maxLength} more characters`);
        } else {
          logger.log(`${prefix}.attr.${attrName}: `, attrValue);
        }
      }
    }
  }

  meta() {
    // If the resource doesn't have a meta, but it has a parent, use the parent's meta
    if (!this.metaInstance && this.hasParentCollection()) {
      return this.getParentCollection().metaInstance;
    }

    return this.metaInstance;
  }

  isTruthy(attrName) {
    return this.attr(attrName) === 'true';
  }

  metaField(attrName, args = {}) {
    const {
      metaOverrideSet = null,
    } = args;

    const field = this.meta() && this.meta().field(attrName);

    if (!field) {
      throw new Error(`meta field ${attrName} missing on resource`);
    }

    let overriddenMetaField;
    if (metaOverrideSet) {
      overriddenMetaField = metaOverrideSet.applyOverrides(field);
    } else {
      overriddenMetaField = field;
    }

    // copy field so we get our own shallow copy of meta for the excludedUnless overrides
    const copyField = new MetaField();
    copyField.shallowCopy(overriddenMetaField);
    return copyField.applyExcludedUnless(attrName, this)
  }

  getParentCollection() {
    if (this.parent() instanceof Collection) {
      return this.parent();
    } else if (this.parent() instanceof ResourceList) {
      return this.parent().parent();
    }
    return null;
  }

  hasParentCollection() {
    return !!this.getParentCollection();
  }

  relationships() {
    return this._relationshipList;
  }

  hasRelationships() {
    return this.relationships().length() > 0;
  }

  /**
   * @deprecated
   * @param key
   * @return {Relationship}
   */
  relationship(key) {
    if (key === undefined) {
      throw new Error('Usage: Key is required.');
    }
    if ((typeof key) !== "string") {
      throw new Error(`Usage: Key must be a string, you provided ${key}.`);
    }

    const relationship = this._relationshipList.item(key);

    if (!relationship) return null;

    return relationship;
  }

  /**
   * @template FoundT
   * @template NotFoundT
   * @param {string} key Relationship key
   * @param {function(Relationship): FoundT} foundFunction Executes if the relationship is found
   * @param {function(): NotFoundT} notFoundFunction Executes if the relationship is not found
   * @return {FoundT|NotFoundT}
   */
  withRelationship(key, foundFunction, notFoundFunction) {
    if (typeof foundFunction !== 'function') {
      throw new Error('Usage: foundFunction is required and must be a function.')
    }
    if (typeof notFoundFunction !== 'function') {
      throw new Error('Usage: notFoundFunction is required and must be a function.')
    }



    const relationship = this.relationship(key)

    if (!!relationship) {
      return foundFunction(relationship)
    }
    return notFoundFunction()
  }

  type() {
    return this.resourceType;
  }

  submit(args = {}) {
    const selfLink = this.link('self');
    const { debug, linkArgs, exportArgs, signal } = args;

    if (this.meta().action('POST')) {
      if (selfLink && debug) {
        return {
          action: 'POST',
          url: selfLink.url()
        };
      } else if (selfLink) {
        return selfLink.Post(null, {
          ...linkArgs,
          exportArgs,
          signal,
        });
      }
    }

    if (selfLink && debug) {
      return {
        action: 'PUT',
        url: selfLink.url()
      };
    } else if (selfLink) {
      return selfLink.Put(null, {
        ...linkArgs,
        exportArgs,
        signal,
      });
    }
    const parentSelfLink = Resource.getParentSelfLink(this);

    if (debug) {
      return {
        action: 'POST',
        url: parentSelfLink.url(),
      };
    }

    if (this.meta().action('PUT')) {
      if (parentSelfLink && debug) {
        return {
          action: 'PUT',
          url: parentSelfLink.url()
        };
      } else if (parentSelfLink) {
        return parentSelfLink.Put(this.export(exportArgs), {
          ...linkArgs,
          signal,
        });
      }
    }

    return parentSelfLink.Post(this.export(exportArgs), { ...linkArgs, signal });
  }

  delete(args) {
    if (!this.link('self')) {
      throw new Error('Must have a self link to delete');
    }

    return this.link('self').Delete(args);
  }

  isAttrEmpty(attrName) {
    if (attrName === undefined) {
      throw new Error('Usage: attrName is required.');
    }

    return isValueEmpty(this.attr(attrName));
  }

  /**
   * Check resource against meta to see if it is in a valid state.
   * WARNING: This is only minimally complete at the moment. Use with caution until this warning is removed.
   */
  validate() {
    let errors = [];

    const template = this.meta().template().list();

    for (let i = 0; i < template.length; i++) {
      const f = template[i];
      const name = f.attr('name');
      const field = this.metaField(name);
      const attrValue = this.attr(name);

      if (field.attr('readonly')) {
        continue;
      }

      if (field.attr('required') && attrValue === undefined) {
        errors.push(new RequiredValueError(name));
        continue;
      }

      switch(field.attr('type')) {
        case 'string':
          if (field.attr('required') && String(attrValue) === "") {
            errors.push(new RequiredValueError(name));
            continue;
          }
          break;
        case 'number':
          if (field.attr('required') && attrValue === '') {
            errors.push(new RequiredValueError(name));
            continue;
          }
          break;
        case 'date':
          if (field.attr('required') && !attrValue) {
            errors.push(new RequiredValueError(name));
            continue;
          }
          break;
        case 'picklist':
          // Can't just check if there is an empty string because some picklists have an empty string as a valid option - must also check if '' is a valid option
          if (field.attr('required') && String(attrValue) === "" && !field.options().map()['']) {
            errors.push(new RequiredValueError(name));
            continue;
          }
          break;
        // no default
      }
      // TODO: Type specific validation (min, max, etc)
    }

    return errors;
  }

  isValid() {
    return this.validate().length === 0;
  }

  // This re-creates the resource
  overrideMeta(metaOverrideSet) {
    return new Resource({
      data: this.export(),
      metaOverrideSet,
    });
  }

  // Clear meta overrides from the resource
  resetMeta() {
    if (this.initialMeta) {
      this.metaInstance = new Meta({ data: this.initialMeta, parent: this });
    } else {
      throw new Error("Resource has no meta to reset. Did you mean to call this on the collection?");
    }
    return this;
  }

  // Only need a prompt on a pick list? use selectedOptionPrompt(attrName)
  selectedOptions(attrName, optionAttrName) {
    if (attrName === undefined) {
      throw new Error('Usage: attrName is required.');
    }
    if (optionAttrName === undefined) {
      throw new Error('Usage: optionAttrName is required.');
    }

    let attrValues = this.attr(attrName);

    if (!Array.isArray(attrValues)) {
      attrValues = [attrValues];
    }

    const metaField = this.meta().field(attrName);
    return attrValues.map(v => {
      const option = metaField.option(v);

      if (option === null) {
        throw new GenericError('invalid-picklist-option', 'Option does not exist in picklist meta options', {
          attrName,
          option: v,
        });
      }

      return option;
    })
    .map(option => {
      return option.attr(optionAttrName);
    });
  }

  selectedOptionPrompt(attrName) {
    return this.selectedOptions(attrName, 'prompt')[0];
  }

  getPromptForAttribute(attrName) {
    const prompt = this.meta().field(attrName).attr('prompt')
    if (!prompt) {
      throw new GenericError('invalid-prompt', 'Attribute or Prompt for attribute does not exist in resource meta', {
        attrName,
      });
    }

    return prompt
  }

  //  Return (IF FOUND) the first available dependent subtype option.
  //  This prevents assigning a mis-matched required subtype, something that can re-assign your resource's type, creating subtle bugs.
  firstAvailableSubTypeOption(type, subTypeFieldName = 'subType') {
    return this.meta().field(subTypeFieldName).options().list()
    .find(option => option.attr('depends') && option.attr('depends').includes(type));
  }

  query(relName, existsFn, doesntExistFn) {
    const query = this.meta().query(relName);

    if (!query) {
      return doesntExistFn();
    }

    const link = new Link({
      data: {
        name: 'self',
        url: query.attr('url'),
      },
      parent: this,
    });

    return existsFn(link, { query });
  }

  getExclusionList() {
    return this._exclusionList;
  }
}

export default Resource;
